import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, Check, X } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import BotonAyuda from '../components/BotonAyuda';

export default function EstructuraCostoGrupos({ embebido = false, onCambio } = {}) {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [grupos, setGrupos] = useState([]);
  const [nuevo, setNuevo] = useState('');
  const [editId, setEditId] = useState(null);
  const [editNombre, setEditNombre] = useState('');
  const [error, setError] = useState('');

  async function cargar() {
    const { data } = await api.get('/estructura-costo-grupo');
    setGrupos(data);
    if (onCambio) onCambio(data);
  }

  useEffect(() => {
    if (empresaActiva) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActiva]);

  async function agregar(e) {
    e.preventDefault();
    setError('');
    if (!nuevo.trim()) return;
    try {
      await api.post('/estructura-costo-grupo', { nombre: nuevo.trim() });
      setNuevo('');
      await cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo crear el grupo');
    }
  }

  async function guardarNombre(id) {
    setError('');
    if (!editNombre.trim()) return;
    try {
      await api.put(`/estructura-costo-grupo/${id}`, { nombre: editNombre.trim() });
      setEditId(null);
      await cargar();
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar');
    }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este grupo? Las líneas de estructura que lo usan quedan "Sin grupo".')) return;
    try {
      await api.delete(`/estructura-costo-grupo/${id}`);
      await cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo eliminar');
    }
  }

  async function mover(indice, dir) {
    const otro = indice + dir;
    if (otro < 0 || otro >= grupos.length) return;
    const a = grupos[indice];
    const b = grupos[otro];
    const items = [
      { id: a.id, orden: b.orden },
      { id: b.id, orden: a.orden }
    ];
    try {
      const { data } = await api.put('/estructura-costo-grupo/orden', { items });
      setGrupos(data);
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo reordenar');
    }
  }

  return (
    <div className={embebido ? 'space-y-4' : 'space-y-6 max-w-2xl'}>
      {!embebido && (
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Grupos de estructura de costo
            {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
            <BotonAyuda />
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Bloques en que se agrupan las líneas de la estructura de costo de cada producto. El orden fija cómo se
            suman y se muestran en el desglose del costo en vivo.
          </p>
        </div>
      )}

      <div className="bg-white border rounded-lg divide-y">
        {grupos.length === 0 && (
          <p className="p-4 text-sm text-slate-400">Sin grupos todavía.</p>
        )}
        {grupos.map((g, i) => (
          <div key={g.id} className="flex items-center gap-3 p-3">
            <div className="flex flex-col">
              <button
                onClick={() => mover(i, -1)}
                disabled={i === 0}
                title="Subir"
                className="text-slate-400 hover:text-primary-600 disabled:opacity-30"
              >
                <ChevronUp size={14} />
              </button>
              <button
                onClick={() => mover(i, 1)}
                disabled={i === grupos.length - 1}
                title="Bajar"
                className="text-slate-400 hover:text-primary-600 disabled:opacity-30"
              >
                <ChevronDown size={14} />
              </button>
            </div>

            {editId === g.id ? (
              <>
                <input
                  autoFocus
                  value={editNombre}
                  onChange={(e) => setEditNombre(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && guardarNombre(g.id)}
                  className="flex-1 border rounded-md px-2 py-1 text-sm"
                />
                <button onClick={() => guardarNombre(g.id)} title="Guardar" className="text-emerald-600 hover:text-emerald-700">
                  <Check size={16} />
                </button>
                <button onClick={() => setEditId(null)} title="Cancelar" className="text-slate-400 hover:text-slate-600">
                  <X size={16} />
                </button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm font-medium text-slate-700">{g.nombre}</span>
                <button
                  onClick={() => { setEditId(g.id); setEditNombre(g.nombre); }}
                  title="Renombrar"
                  className="text-slate-400 hover:text-primary-600"
                >
                  <Pencil size={15} />
                </button>
                <button onClick={() => eliminar(g.id)} title="Eliminar" className="text-slate-400 hover:text-red-600">
                  <Trash2 size={15} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      <form onSubmit={agregar} className="flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-xs text-slate-500 mb-1">Nuevo grupo</label>
          <input
            value={nuevo}
            onChange={(e) => setNuevo(e.target.value)}
            placeholder="ej. Mano de obra"
            className="w-full border rounded-md px-3 py-2 text-sm"
          />
        </div>
        <button type="submit" title="Agregar grupo" className="bg-primary-600 hover:bg-primary-700 text-white p-2 rounded-md">
          <Plus size={18} />
        </button>
      </form>
      {error && <p className="text-red-600 text-sm">{error}</p>}
    </div>
  );
}
